import getArea, { AreaTelefonica } from "./getArea";

export interface ParsedPhone {
  area: AreaTelefonica;
  /** Área sin 0 y número sin 15: siempre 10 dígitos. */
  number: string;
  isMobile: boolean;
}

const SEPARATORS = /[\s()\-.+]/g;
const NATIONAL_DIGITS_LENGTH = 10;
const MOBILE_PREFIX = "15";

/** El código de área exacto, no el más largo que empiece igual. */
const exactArea = (code: string): AreaTelefonica | null => {
  const area = getArea(code);
  return area?.Code === code ? area : null;
};

/**
 * El número nacional sin el 0 de adelante: área + número, o área + 15 + número.
 * Ningún código de área es otro código seguido de un 1, así que el 15 puede
 * estar en un solo lugar.
 */
const parseNational = (
  national: string
): {
  area: AreaTelefonica;
  number: string;
  hasMobilePrefix: boolean;
} | null => {
  if (!/^[123]/.test(national)) return null;

  if (national.length === NATIONAL_DIGITS_LENGTH) {
    const area = getArea(`0${national}`);
    return area ? { area, number: national, hasMobilePrefix: false } : null;
  }

  if (national.length === NATIONAL_DIGITS_LENGTH + MOBILE_PREFIX.length) {
    for (let areaLength = 2; areaLength <= 4; areaLength++) {
      const area = exactArea(`0${national.substring(0, areaLength)}`);
      const prefix = national.substring(areaLength, areaLength + 2);

      if (area && prefix === MOBILE_PREFIX) {
        const number =
          national.substring(0, areaLength) +
          national.substring(areaLength + 2);
        return { area, number, hasMobilePrefix: true };
      }
    }
  }

  return null;
};

/** De otro país: empieza con + y no sigue con 54. */
export const isForeignPhone = (value?: string | null): boolean => {
  const text = String(value ?? "");
  return /^[\s(]*\+/.test(text) && !text.replace(/\D/g, "").startsWith("54");
};

/**
 * Interpreta un teléfono argentino escrito o guardado en cualquiera de los
 * formatos que conviven en los datos (con o sin 0, con o sin 15, con 54 o 549)
 * y con cualquier separador. Si no se puede interpretar devuelve null.
 *
 * Con 549 o con 15 es celular y con 54 sin 9 es fijo. Si no tiene 54 ni 15
 * se usa `isMobile`, que por defecto es celular.
 */
const parsePhone = (
  value?: string | null,
  isMobile: boolean = true
): ParsedPhone | null => {
  // Hay datos guardados como number aunque el tipo diga string.
  const text = String(value ?? "");
  const digits = text.replace(SEPARATORS, "");
  if (!/^\d+$/.test(digits)) return null;

  // Con + adelante y sin 54 es de otro país, aunque los dígitos parezcan un
  // área argentina: +354 611 2345 (Islandia) no es el área 03546.
  if (isForeignPhone(text)) return null;

  // Ningún código de área empieza con 5, así que un 54 adelante con al menos
  // 10 dígitos más es siempre el código del país.
  if (digits.startsWith("54") && digits.length >= 12) {
    const hasNine = digits[2] === "9";
    const rest = digits.substring(hasNine ? 3 : 2);
    const national = parseNational(
      rest.startsWith("0") ? rest.substring(1) : rest
    );
    if (!national) return null;

    return {
      area: national.area,
      number: national.number,
      isMobile: hasNine || national.hasMobilePrefix,
    };
  }

  const national = parseNational(
    digits.startsWith("0") ? digits.substring(1) : digits
  );
  if (!national) return null;

  return {
    area: national.area,
    number: national.number,
    isMobile: national.hasMobilePrefix || isMobile,
  };
};

/** 549 + área sin 0 + número sin 15 para celulares, 54 + área + número para fijos. */
export const toInternationalPhone = (
  value?: string | null,
  isMobile?: boolean
): string | null => {
  const phone = parsePhone(value, isMobile);
  if (!phone) return null;

  return `${phone.isMobile ? "549" : "54"}${phone.number}`;
};

/**
 * Lo que se manda al backend: el internacional si se puede interpretar y, si
 * no, el valor como está, para no bloquear un teléfono guardado que nadie tocó.
 * Con `onlyMobile` va siempre con 549, aunque esté guardado como fijo.
 * `isMobile` es el tipo cuando el número no tiene 54 ni 15.
 */
export const toPhoneToSend = <T extends string | null | undefined>(
  value: T,
  onlyMobile: boolean = false,
  isMobile: boolean = true
): string | T => {
  const phone = parsePhone(value, isMobile);
  if (!phone) return value;

  return `${onlyMobile || phone.isMobile ? "549" : "54"}${phone.number}`;
};

/**
 * La póliza tiene solo el teléfono fijo: el que se precarga es ese y se manda
 * como fijo.
 */
export const hasOnlyLandline = (
  asegurado?: {
    telefonoCelular?: string | null;
    telefonoFijo?: string | null;
  } | null
): boolean => !asegurado?.telefonoCelular && !!asegurado?.telefonoFijo;

/**
 * El número para el link de WhatsApp. Con `onlyMobile` va con 549 aunque esté
 * guardado con 54 sin 9: WhatsApp no abre un celular argentino sin el 9. Si no
 * se puede interpretar se arma como antes, sacando el 0 de adelante y el
 * primer 15, para no esconder el ícono.
 */
export const toWhatsappPhone = (
  value?: string | null,
  onlyMobile: boolean = false
): string => {
  const phone = parsePhone(value);
  if (phone)
    return `${onlyMobile || phone.isMobile ? "549" : "54"}${phone.number}`;

  return `54${String(value ?? "")
    .replace(/^0/, "")
    .replace("15", "")
    .replace(/\s/g, "")}`;
};

/** Con 0 y, si es celular, con 15: lo que muestra y edita PhoneInput. */
export const toLocalPhone = (phone: ParsedPhone): string => {
  const areaLength = phone.area.Code.length - 1;
  const area = phone.number.substring(0, areaLength);
  const subscriber = phone.number.substring(areaLength);

  return `0${area}${phone.isMobile ? MOBILE_PREFIX : ""}${subscriber}`;
};

export default parsePhone;
